import { Field, TextInput, TextArea, BilingualText, PhotoField, ListEditor, Section } from "./FormFields";
import RichTextEditor from "./RichTextEditor";

// Helper: bilingual change handler
const blChange = (data, key, sub, lang, value, setData) => {
  setData({ ...data, [key]: { ...data[key], [lang]: { ...data[key]?.[lang], [sub]: value } } });
};

// =================== HERO ===================
export const HeroForm = ({ data, setData }) => {
  const ru = data.ru || {};
  const en = data.en || {};
  const set = (lang, key, value) => setData({ ...data, [lang]: { ...data[lang], [key]: value } });
  return (
    <Section title="Главный блок (Hero)" description="Первый экран сайта — самое важное.">
      <BilingualText label="Маленький бэйдж сверху" ruValue={ru.eyebrow} enValue={en.eyebrow}
        onChange={(l, v) => set(l, "eyebrow", v)} />
      <BilingualText label="Заголовок" ruValue={ru.title} enValue={en.title}
        onChange={(l, v) => set(l, "title", v)} multiline rows={2} />
      <BilingualText label="Подзаголовок (описание)" ruValue={ru.subtitle} enValue={en.subtitle}
        onChange={(l, v) => set(l, "subtitle", v)} multiline rows={2} />
      <BilingualText label="Главная кнопка" ruValue={ru.cta_primary} enValue={en.cta_primary}
        onChange={(l, v) => set(l, "cta_primary", v)} />
      <BilingualText label="Вторая кнопка" ruValue={ru.cta_secondary} enValue={en.cta_secondary}
        onChange={(l, v) => set(l, "cta_secondary", v)} />
    </Section>
  );
};

// =================== STATS ===================
export const StatsForm = ({ data, setData }) => (
  <Section title="Статистика" description="Цифры показатели на главной (например, 100% контракты).">
    <ListEditor
      label="Показатели"
      items={data}
      onChange={setData}
      defaultItem={{ value: "10", suffix: "+", label_ru: "новый показатель", label_en: "new metric" }}
      itemTitle={(it) => `${it.value}${it.suffix} · ${it.label_ru}`}
      renderItem={(it, update) => (
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Число"><TextInput value={it.value} onChange={(v) => update({ value: v })} placeholder="100" /></Field>
          <Field label="Символ после (%, +, …)"><TextInput value={it.suffix} onChange={(v) => update({ suffix: v })} placeholder="%" /></Field>
          <Field label="Подпись (RU)"><TextInput value={it.label_ru} onChange={(v) => update({ label_ru: v })} /></Field>
          <Field label="Подпись (EN)"><TextInput value={it.label_en} onChange={(v) => update({ label_en: v })} /></Field>
        </div>
      )}
    />
  </Section>
);

// =================== BENEFITS ===================
const ICONS = ["FileSignature", "Handshake", "BadgeEuro", "FileCheck", "ShieldCheck", "Briefcase", "Plane", "Users", "Globe2", "Star", "Award", "Heart", "Sparkles", "Rocket", "Trophy"];

export const BenefitsForm = ({ data, setData }) => {
  const setHeader = (lang, key, v) => setData({ ...data, [lang]: { ...data[lang], [key]: v } });
  return (
    <>
      <Section title="Заголовок секции «Преимущества»">
        <BilingualText label="Заголовок" ruValue={data.ru?.title} enValue={data.en?.title} onChange={(l, v) => setHeader(l, "title", v)} />
        <BilingualText label="Подзаголовок" ruValue={data.ru?.subtitle} enValue={data.en?.subtitle} onChange={(l, v) => setHeader(l, "subtitle", v)} multiline rows={2} />
      </Section>
      <Section title="Карточки преимуществ" description="Что выгодно знать клиенту.">
        <ListEditor
          items={data.items}
          onChange={(items) => setData({ ...data, items })}
          defaultItem={{ icon: "Sparkles", ru_title: "Заголовок", ru_text: "Описание", en_title: "Title", en_text: "Description" }}
          itemTitle={(it) => it.ru_title || "Новая карточка"}
          renderItem={(it, update) => (
            <div className="space-y-3">
              <Field label="Иконка">
                <select value={it.icon} onChange={(e) => update({ icon: e.target.value })} className="w-full h-11 px-4 rounded-xl border border-input bg-background text-sm">
                  {ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                </select>
              </Field>
              <BilingualText label="Заголовок карточки" ruValue={it.ru_title} enValue={it.en_title} onChange={(l, v) => update({ [l === "ru" ? "ru_title" : "en_title"]: v })} />
              <BilingualText label="Описание" ruValue={it.ru_text} enValue={it.en_text} onChange={(l, v) => update({ [l === "ru" ? "ru_text" : "en_text"]: v })} multiline rows={2} />
            </div>
          )}
        />
      </Section>
    </>
  );
};

// =================== PRICING ===================
export const PricingForm = ({ data, setData }) => {
  const setHeader = (lang, key, v) => setData({ ...data, [lang]: { ...data[lang], [key]: v } });
  return (
    <>
      <Section title="Заголовок секции «Тарифы»">
        <BilingualText label="Заголовок" ruValue={data.ru?.title} enValue={data.en?.title} onChange={(l, v) => setHeader(l, "title", v)} />
        <BilingualText label="Подзаголовок" ruValue={data.ru?.subtitle} enValue={data.en?.subtitle} onChange={(l, v) => setHeader(l, "subtitle", v)} multiline rows={2} />
      </Section>
      <Section title="Тарифные планы">
        <ListEditor
          items={data.plans}
          onChange={(plans) => setData({ ...data, plans })}
          defaultItem={{ ru_name: "Новый тариф", en_name: "New plan", ru_desc: "", en_desc: "", price: "0", currency: "£", free: false, featured: false, ru_features: [], en_features: [], ru_cta: "Узнать детали", en_cta: "Learn more" }}
          itemTitle={(it) => `${it.ru_name} · ${it.free ? "Бесплатно" : (it.price + it.currency)}`}
          renderItem={(it, update) => (
            <div className="space-y-3">
              <BilingualText label="Название тарифа" ruValue={it.ru_name} enValue={it.en_name} onChange={(l, v) => update({ [l === "ru" ? "ru_name" : "en_name"]: v })} />
              <BilingualText label="Краткое описание" ruValue={it.ru_desc} enValue={it.en_desc} onChange={(l, v) => update({ [l === "ru" ? "ru_desc" : "en_desc"]: v })} />
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Цена"><TextInput value={it.price} onChange={(v) => update({ price: v })} /></Field>
                <Field label="Валюта (символ)"><TextInput value={it.currency} onChange={(v) => update({ currency: v })} placeholder="£ € $" /></Field>
                <Field label="Тип">
                  <select value={it.free ? "free" : "paid"} onChange={(e) => update({ free: e.target.value === "free" })} className="w-full h-11 px-4 rounded-xl border border-input bg-background text-sm">
                    <option value="paid">Платный</option>
                    <option value="free">Бесплатный</option>
                  </select>
                </Field>
              </div>
              <Field>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!it.featured} onChange={(e) => update({ featured: e.target.checked })} className="size-4 rounded" />
                  Выделить как рекомендуемый
                </label>
              </Field>
              <BilingualText label="Текст на кнопке" ruValue={it.ru_cta} enValue={it.en_cta} onChange={(l, v) => update({ [l === "ru" ? "ru_cta" : "en_cta"]: v })} />
              <Field label="Что входит (RU) — каждый пункт с новой строки">
                <TextArea rows={4} value={(it.ru_features || []).join("\n")} onChange={(v) => update({ ru_features: v.split("\n").filter((x) => x.trim()) })} />
              </Field>
              <Field label="What's included (EN) — one item per line">
                <TextArea rows={4} value={(it.en_features || []).join("\n")} onChange={(v) => update({ en_features: v.split("\n").filter((x) => x.trim()) })} />
              </Field>
            </div>
          )}
        />
      </Section>
    </>
  );
};

// =================== PROCESS ===================
export const ProcessForm = ({ data, setData }) => {
  const setHeader = (lang, key, v) => setData({ ...data, [lang]: { ...data[lang], [key]: v } });
  return (
    <>
      <Section title="Заголовок секции «Процесс»">
        <BilingualText label="Заголовок" ruValue={data.ru?.title} enValue={data.en?.title} onChange={(l, v) => setHeader(l, "title", v)} />
        <BilingualText label="Подзаголовок" ruValue={data.ru?.subtitle} enValue={data.en?.subtitle} onChange={(l, v) => setHeader(l, "subtitle", v)} multiline rows={2} />
      </Section>
      <Section title="Шаги процесса">
        <ListEditor
          items={data.steps}
          onChange={(steps) => setData({ ...data, steps })}
          defaultItem={{ ru_title: "Шаг", en_title: "Step", ru_text: "", en_text: "" }}
          itemTitle={(it, i) => `${i + 1}. ${it.ru_title}`}
          renderItem={(it, update) => (
            <div className="space-y-3">
              <BilingualText label="Название шага" ruValue={it.ru_title} enValue={it.en_title} onChange={(l, v) => update({ [l === "ru" ? "ru_title" : "en_title"]: v })} />
              <BilingualText label="Описание" ruValue={it.ru_text} enValue={it.en_text} onChange={(l, v) => update({ [l === "ru" ? "ru_text" : "en_text"]: v })} multiline rows={2} />
            </div>
          )}
        />
      </Section>
    </>
  );
};

// =================== COUNTRIES ===================
export const CountriesForm = ({ data, setData }) => {
  const setHeader = (lang, key, v) => setData({ ...data, [lang]: { ...data[lang], [key]: v } });
  return (
    <>
      <Section title="Заголовок секции «Направления»">
        <BilingualText label="Заголовок" ruValue={data.ru?.title} enValue={data.en?.title} onChange={(l, v) => setHeader(l, "title", v)} />
        <BilingualText label="Подзаголовок" ruValue={data.ru?.subtitle} enValue={data.en?.subtitle} onChange={(l, v) => setHeader(l, "subtitle", v)} />
      </Section>
      <Section title="Карточки стран">
        <ListEditor
          items={data.items}
          onChange={(items) => setData({ ...data, items })}
          defaultItem={{ ru_name: "Страна", en_name: "Country", ru_jobs: "", en_jobs: "", salary: "€0", image: "" }}
          itemTitle={(it) => `${it.ru_name} · ${it.salary}`}
          renderItem={(it, update) => (
            <div className="space-y-3">
              <BilingualText label="Название страны" ruValue={it.ru_name} enValue={it.en_name} onChange={(l, v) => update({ [l === "ru" ? "ru_name" : "en_name"]: v })} />
              <BilingualText label="Типы работ" ruValue={it.ru_jobs} enValue={it.en_jobs} onChange={(l, v) => update({ [l === "ru" ? "ru_jobs" : "en_jobs"]: v })} />
              <Field label="Средняя зарплата"><TextInput value={it.salary} onChange={(v) => update({ salary: v })} placeholder="€2000–2800" /></Field>
              <PhotoField label="Фото страны" value={it.image} onChange={(v) => update({ image: v })} accept="image/*" />
            </div>
          )}
        />
      </Section>
    </>
  );
};

// =================== VACANCIES ===================
export const VacanciesForm = ({ data, setData }) => {
  const setHeader = (lang, key, v) => setData({ ...data, [lang]: { ...data[lang], [key]: v } });
  return (
    <>
      <Section title="Заголовок секции «Вакансии»">
        <BilingualText label="Заголовок" ruValue={data.ru?.title} enValue={data.en?.title} onChange={(l, v) => setHeader(l, "title", v)} />
        <BilingualText label="Подзаголовок" ruValue={data.ru?.subtitle} enValue={data.en?.subtitle} onChange={(l, v) => setHeader(l, "subtitle", v)} />
        <BilingualText label="CTA — заголовок" ruValue={data.ru?.cta_title} enValue={data.en?.cta_title} onChange={(l, v) => setHeader(l, "cta_title", v)} />
        <BilingualText label="CTA — описание" ruValue={data.ru?.cta_text} enValue={data.en?.cta_text} onChange={(l, v) => setHeader(l, "cta_text", v)} multiline rows={2} />
        <BilingualText label="CTA — кнопка" ruValue={data.ru?.cta_button} enValue={data.en?.cta_button} onChange={(l, v) => setHeader(l, "cta_button", v)} />
        <Field label="Telegram-ссылка (для CTA)"><TextInput value={data.telegram} onChange={(v) => setData({ ...data, telegram: v })} placeholder="https://t.me/..." /></Field>
      </Section>
      <Section title="Категории вакансий">
        <ListEditor
          items={data.items}
          onChange={(items) => setData({ ...data, items })}
          defaultItem={{ ru_title: "Категория", en_title: "Category", ru_desc: "", en_desc: "", ru_bullets: [], en_bullets: [] }}
          itemTitle={(it) => it.ru_title}
          renderItem={(it, update) => (
            <div className="space-y-3">
              <BilingualText label="Название" ruValue={it.ru_title} enValue={it.en_title} onChange={(l, v) => update({ [l === "ru" ? "ru_title" : "en_title"]: v })} />
              <BilingualText label="Краткое описание" ruValue={it.ru_desc} enValue={it.en_desc} onChange={(l, v) => update({ [l === "ru" ? "ru_desc" : "en_desc"]: v })} />
              <Field label="Особенности (RU) — каждая с новой строки">
                <TextArea rows={3} value={(it.ru_bullets || []).join("\n")} onChange={(v) => update({ ru_bullets: v.split("\n").filter((x) => x.trim()) })} />
              </Field>
              <Field label="Bullets (EN)">
                <TextArea rows={3} value={(it.en_bullets || []).join("\n")} onChange={(v) => update({ en_bullets: v.split("\n").filter((x) => x.trim()) })} />
              </Field>
            </div>
          )}
        />
      </Section>
    </>
  );
};

// =================== SIMPLE BILINGUAL HEADER BLOCKS ===================
export const TeamSectionForm = ({ data, setData }) => {
  const set = (lang, key, v) => setData({ ...data, [lang]: { ...data[lang], [key]: v } });
  return (
    <Section title="Секция «Команда» — заголовок" description="Сами сотрудники редактируются в разделе «Команда».">
      <BilingualText label="Заголовок" ruValue={data.ru?.title} enValue={data.en?.title} onChange={(l, v) => set(l, "title", v)} />
      <BilingualText label="Подзаголовок" ruValue={data.ru?.subtitle} enValue={data.en?.subtitle} onChange={(l, v) => set(l, "subtitle", v)} multiline rows={2} />
    </Section>
  );
};

export const ReviewsSectionForm = ({ data, setData }) => {
  const set = (lang, key, v) => setData({ ...data, [lang]: { ...data[lang], [key]: v } });
  return (
    <Section title="Секция «Отзывы» — тексты" description="Сами отзывы редактируются в разделе «Отзывы».">
      <BilingualText label="Заголовок" ruValue={data.ru?.title} enValue={data.en?.title} onChange={(l, v) => set(l, "title", v)} />
      <BilingualText label="Подзаголовок" ruValue={data.ru?.subtitle} enValue={data.en?.subtitle} onChange={(l, v) => set(l, "subtitle", v)} multiline rows={2} />
      <BilingualText label="Кнопка «Оставить отзыв»" ruValue={data.ru?.leave_btn} enValue={data.en?.leave_btn} onChange={(l, v) => set(l, "leave_btn", v)} />
      <BilingualText label="Заголовок формы" ruValue={data.ru?.form_title} enValue={data.en?.form_title} onChange={(l, v) => set(l, "form_title", v)} />
      <BilingualText label="Заголовок страницы благодарности" ruValue={data.ru?.form_thanks_title} enValue={data.en?.form_thanks_title} onChange={(l, v) => set(l, "form_thanks_title", v)} />
      <BilingualText label="Текст страницы благодарности" ruValue={data.ru?.form_thanks_text} enValue={data.en?.form_thanks_text} onChange={(l, v) => set(l, "form_thanks_text", v)} multiline rows={3} />
    </Section>
  );
};

// =================== CONTACTS ===================
export const ContactsForm = ({ data, setData }) => {
  const set = (lang, key, v) => setData({ ...data, [lang]: { ...data[lang], [key]: v } });
  return (
    <>
      <Section title="Тексты секции «Контакты»">
        <BilingualText label="Заголовок" ruValue={data.ru?.title} enValue={data.en?.title} onChange={(l, v) => set(l, "title", v)} />
        <BilingualText label="Подзаголовок" ruValue={data.ru?.subtitle} enValue={data.en?.subtitle} onChange={(l, v) => set(l, "subtitle", v)} multiline rows={2} />
        <BilingualText label="Описание компании" ruValue={data.ru?.company_desc} enValue={data.en?.company_desc} onChange={(l, v) => set(l, "company_desc", v)} multiline rows={3} />
        <BilingualText label="Текст на кнопке «Лицензия»" ruValue={data.ru?.license_btn} enValue={data.en?.license_btn} onChange={(l, v) => set(l, "license_btn", v)} />
      </Section>
      <Section title="Каналы связи">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="WhatsApp (номер для отображения)"><TextInput value={data.whatsapp} onChange={(v) => setData({ ...data, whatsapp: v })} placeholder="+44..." /></Field>
          <Field label="WhatsApp-ссылка"><TextInput value={data.whatsapp_link} onChange={(v) => setData({ ...data, whatsapp_link: v })} placeholder="https://wa.me/..." /></Field>
          <Field label="Telegram (ник)"><TextInput value={data.telegram} onChange={(v) => setData({ ...data, telegram: v })} placeholder="@..." /></Field>
          <Field label="Telegram-ссылка"><TextInput value={data.telegram_link} onChange={(v) => setData({ ...data, telegram_link: v })} placeholder="https://t.me/..." /></Field>
          <Field label="Email"><TextInput value={data.email} onChange={(v) => setData({ ...data, email: v })} type="email" /></Field>
          <Field label="Адрес офиса"><TextInput value={data.address} onChange={(v) => setData({ ...data, address: v })} /></Field>
          <Field label="Юр. название компании"><TextInput value={data.company_name} onChange={(v) => setData({ ...data, company_name: v })} /></Field>
          <Field label="Копирайт (внизу страницы)"><TextInput value={data.copyright} onChange={(v) => setData({ ...data, copyright: v })} /></Field>
        </div>
      </Section>
    </>
  );
};

// =================== PARTNERS ===================
export const PartnersForm = ({ data, setData }) => (
  <Section title="Логотипы партнёров" description="Логотипы компаний-работодателей в ленте.">
    <ListEditor
      items={data.items}
      onChange={(items) => setData({ ...data, items })}
      defaultItem={{ name: "Компания", logo: "" }}
      itemTitle={(it) => it.name}
      renderItem={(it, update) => (
        <div className="space-y-3">
          <Field label="Название"><TextInput value={it.name} onChange={(v) => update({ name: v })} /></Field>
          <PhotoField label="Логотип" value={it.logo} onChange={(v) => update({ logo: v })} accept="image/*" />
        </div>
      )}
    />
  </Section>
);

// =================== PAYMENT ===================
export const PaymentForm = ({ data, setData }) => {
  const set = (lang, key, v) => setData({ ...data, [lang]: { ...data[lang], [key]: v } });
  return (
    <>
      <Section title="Тексты страницы «Оплата»">
        <BilingualText label="Маленький бэйдж" ruValue={data.ru?.eyebrow} enValue={data.en?.eyebrow} onChange={(l, v) => set(l, "eyebrow", v)} />
        <BilingualText label="Заголовок страницы" ruValue={data.ru?.title} enValue={data.en?.title} onChange={(l, v) => set(l, "title", v)} multiline rows={2} />
        <BilingualText label="Описание" ruValue={data.ru?.subtitle} enValue={data.en?.subtitle} onChange={(l, v) => set(l, "subtitle", v)} multiline rows={3} />
        <BilingualText label="Заголовок «Что входит»" ruValue={data.ru?.checkpoints_title} enValue={data.en?.checkpoints_title} onChange={(l, v) => set(l, "checkpoints_title", v)} />
        <Field label="Пункты «Что входит» (RU) — каждый с новой строки">
          <TextArea rows={6} value={(data.ru?.checkpoints || []).join("\n")} onChange={(v) => setData({ ...data, ru: { ...data.ru, checkpoints: v.split("\n").filter((x) => x.trim()) } })} />
        </Field>
        <Field label="Checkpoints (EN)">
          <TextArea rows={6} value={(data.en?.checkpoints || []).join("\n")} onChange={(v) => setData({ ...data, en: { ...data.en, checkpoints: v.split("\n").filter((x) => x.trim()) } })} />
        </Field>
        <BilingualText label="Текст про политику возврата" ruValue={data.ru?.refund_note} enValue={data.en?.refund_note} onChange={(l, v) => set(l, "refund_note", v)} multiline rows={2} />
        <BilingualText label="Информационная плашка снизу" ruValue={data.ru?.info_note} enValue={data.en?.info_note} onChange={(l, v) => set(l, "info_note", v)} multiline rows={3} />
      </Section>
      <Section title="Подписи формы оплаты">
        <BilingualText label="«Защищённая оплата»" ruValue={data.ru?.secure_label} enValue={data.en?.secure_label} onChange={(l, v) => set(l, "secure_label", v)} />
        <BilingualText label="«Реквизиты платежа»" ruValue={data.ru?.details_title} enValue={data.en?.details_title} onChange={(l, v) => set(l, "details_title", v)} />
        <BilingualText label="Поле «ФИО»" ruValue={data.ru?.name_label} enValue={data.en?.name_label} onChange={(l, v) => set(l, "name_label", v)} />
        <BilingualText label="Поле «Сумма»" ruValue={data.ru?.amount_label} enValue={data.en?.amount_label} onChange={(l, v) => set(l, "amount_label", v)} />
        <BilingualText label="Поле «Валюта»" ruValue={data.ru?.currency_label} enValue={data.en?.currency_label} onChange={(l, v) => set(l, "currency_label", v)} />
        <BilingualText label="Поле «Комментарий»" ruValue={data.ru?.comment_label} enValue={data.en?.comment_label} onChange={(l, v) => set(l, "comment_label", v)} />
        <BilingualText label="Плейсхолдер «Комментарий»" ruValue={data.ru?.comment_placeholder} enValue={data.en?.comment_placeholder} onChange={(l, v) => set(l, "comment_placeholder", v)} />
        <BilingualText label="«Итого к оплате»" ruValue={data.ru?.total_label} enValue={data.en?.total_label} onChange={(l, v) => set(l, "total_label", v)} />
        <BilingualText label="Текст кнопки" ruValue={data.ru?.submit_label} enValue={data.en?.submit_label} onChange={(l, v) => set(l, "submit_label", v)} />
        <BilingualText label="Подпись под кнопкой" ruValue={data.ru?.secure_note} enValue={data.en?.secure_note} onChange={(l, v) => set(l, "secure_note", v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Валюта по умолчанию">
            <select value={data.default_currency} onChange={(e) => setData({ ...data, default_currency: e.target.value })} className="w-full h-11 px-4 rounded-xl border border-input bg-background text-sm">
              <option value="GBP">GBP £</option>
              <option value="EUR">EUR €</option>
              <option value="USD">USD $</option>
            </select>
          </Field>
          <Field label="Сумма по умолчанию (опц.)" hint="Оставьте пустым, чтобы человек вводил свою сумму.">
            <TextInput value={data.default_amount} onChange={(v) => setData({ ...data, default_amount: v })} placeholder="170" />
          </Field>
        </div>
      </Section>
    </>
  );
};

// =================== LEGAL ===================
export const LegalForm = ({ data, setData }) => {
  const set = (key, v) => setData({ ...data, [key]: v });
  return (
    <div className="space-y-6">
      <Section title="Политика конфиденциальности">
        <RichTextEditor label="Русская версия" value={data.privacy_ru} onChange={(v) => set("privacy_ru", v)} />
        <RichTextEditor label="English version" value={data.privacy_en} onChange={(v) => set("privacy_en", v)} />
      </Section>
      <Section title="Пользовательское соглашение">
        <RichTextEditor label="Русская версия" value={data.terms_ru} onChange={(v) => set("terms_ru", v)} />
        <RichTextEditor label="English version" value={data.terms_en} onChange={(v) => set("terms_en", v)} />
      </Section>
      <Section title="Политика возврата">
        <RichTextEditor label="Русская версия" value={data.refund_ru} onChange={(v) => set("refund_ru", v)} />
        <RichTextEditor label="English version" value={data.refund_en} onChange={(v) => set("refund_en", v)} />
      </Section>
    </div>
  );
};

// =================== LICENSE ===================
export const LicenseForm = ({ data, setData }) => (
  <Section title="Лицензия (PDF)" description="Файл будет открываться в новой вкладке при клике на кнопку «Лицензия» в шапке сайта.">
    <PhotoField label="Файл лицензии" value={data.file_url} onChange={(v) => setData({ ...data, file_url: v })} accept="application/pdf,image/*" hint="Поддерживаются PDF и изображения." />
  </Section>
);

// =================== VISIBILITY ===================
const SECTION_LABELS = {
  stats: { title: "Статистика", desc: "Цифры показатели (100% контракты, 1500+ кандидатов и т.д.)" },
  benefits: { title: "Преимущества", desc: "Четыре карточки с иконками" },
  partners: { title: "Партнёры (бегущая лента)", desc: "Логотипы DHL, Milka, Lidl и других работодателей" },
  pricing: { title: "Тарифы", desc: "Три плана со стоимостью" },
  process: { title: "Процесс", desc: "Четыре шага трудоустройства" },
  countries: { title: "Страны", desc: "Карточки направлений с фото и зарплатами" },
  vacancies: { title: "Вакансии", desc: "Категории работ и CTA в Telegram" },
  team: { title: "Команда", desc: "Фото и должности сотрудников" },
  reviews: { title: "Отзывы (превью)", desc: "Три отзыва + кнопка «Смотреть все»" },
  contacts: { title: "Контакты", desc: "WhatsApp, Telegram, Email, адрес, лицензия" },
  payment: { title: "Страница оплаты", desc: "Страница /payment, кнопки «Оплата» в шапке и тарифах" },
};

export const VisibilityForm = ({ data, setData }) => {
  const toggle = (key) => setData({ ...(data || {}), [key]: !(data?.[key] !== false) });
  const setAll = (value) => {
    const next = {};
    Object.keys(SECTION_LABELS).forEach((k) => { next[k] = value; });
    setData(next);
  };
  return (
    <Section title="Видимость блоков на главной" description="Включайте и выключайте блоки. После переключателей нажмите «Сохранить» вверху страницы. Шапка, главный экран и подвал показываются всегда.">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setAll(true)}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-border text-xs font-semibold hover:bg-muted"
          data-testid="visibility-enable-all"
        >
          ✓ Включить все
        </button>
        <button
          type="button"
          onClick={() => setAll(false)}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-border text-xs font-semibold hover:bg-muted"
          data-testid="visibility-disable-all"
        >
          ✕ Выключить все
        </button>
      </div>
      <div className="space-y-2.5">
        {Object.entries(SECTION_LABELS).map(([key, info]) => {
          const enabled = data?.[key] !== false;
          return (
            <label
              key={key}
              htmlFor={`vis-${key}`}
              className={`flex items-center justify-between gap-4 p-4 rounded-2xl border cursor-pointer transition-colors ${enabled ? "border-border bg-card hover:bg-muted/40" : "border-border/60 bg-muted/40 hover:bg-muted/60"}`}
            >
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-sm transition-colors ${enabled ? "text-foreground" : "text-muted-foreground"}`}>{info.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{info.desc}</div>
              </div>
              <button
                id={`vis-${key}`}
                type="button"
                onClick={(e) => { e.preventDefault(); toggle(key); }}
                role="switch"
                aria-checked={enabled}
                data-testid={`visibility-toggle-${key}`}
                style={{
                  width: 52,
                  height: 30,
                  borderRadius: 999,
                  background: enabled ? "hsl(var(--accent))" : "hsl(var(--muted-foreground) / 0.35)",
                  position: "relative",
                  flexShrink: 0,
                  transition: "background-color 0.2s",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 3,
                    left: enabled ? 25 : 3,
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "#fff",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                    transition: "left 0.2s",
                  }}
                />
              </button>
            </label>
          );
        })}
      </div>
    </Section>
  );
};

// =================== FOOTER BANNER ===================
export const FooterBannerForm = ({ data, setData }) => {
  const d = data || {};
  const set = (k, v) => setData({ ...d, [k]: v });
  const example = `<a href="https://merchant.freekassa.net" target="_blank" rel="noopener noreferrer">\n  <img src="https://cdn.merchant.freekassa.net/banners/big-dark-1.png" title="Прием платежей" alt="FreeKassa">\n</a>`;
  return (
    <Section
      title="Баннер в подвале сайта"
      description="Вставьте сюда HTML-код баннера от платёжной системы (FreeKassa и т.п.). После сохранения он автоматически записывается в исходный HTML сайта — именно туда, где его ищет бот платёжной системы для верификации. На проде изменения появятся после следующего деплоя."
    >
      <Field label="Показывать баннер">
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <button
            type="button"
            onClick={() => set("enabled", !d.enabled)}
            role="switch"
            aria-checked={!!d.enabled}
            data-testid="footer-banner-toggle"
            style={{
              width: 52, height: 30, borderRadius: 999,
              background: d.enabled ? "hsl(var(--accent))" : "hsl(var(--muted-foreground) / 0.35)",
              position: "relative", border: "none", padding: 0, cursor: "pointer",
              transition: "background-color 0.2s",
            }}
          >
            <span style={{
              position: "absolute", top: 3, left: d.enabled ? 25 : 3,
              width: 24, height: 24, borderRadius: "50%", background: "#fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)", transition: "left 0.2s",
            }} />
          </button>
          <span className="text-sm font-medium">{d.enabled ? "Включён" : "Выключен"}</span>
        </label>
      </Field>

      <Field
        label="HTML-код баннера"
        hint="Полный код от вашей платёжной системы — обычно <a>…<img>…</a>. Скрипты (<script>) и обработчики on*= удаляются автоматически."
      >
        <textarea
          value={d.html || ""}
          onChange={(e) => set("html", e.target.value)}
          rows={7}
          spellCheck={false}
          placeholder={example}
          className="w-full px-4 py-3 rounded-xl border border-input bg-background text-sm font-mono leading-relaxed"
          data-testid="footer-banner-html"
        />
      </Field>

      {d.html && (
        <Field label="Предпросмотр" hint="Так баннер выглядит в правом нижнем углу сайта.">
          <div className="p-6 rounded-xl border border-border bg-gradient-to-br from-muted/40 to-muted/10 flex items-center justify-center min-h-[100px]">
            <div
              className="[&_img]:max-w-[240px] [&_img]:h-auto [&_img]:block [&_a]:inline-block [&_a]:rounded-lg [&_a]:overflow-hidden"
              dangerouslySetInnerHTML={{ __html: stripScripts(d.html) }}
            />
          </div>
        </Field>
      )}

      <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 text-xs leading-relaxed text-amber-700 dark:text-amber-400">
        <div className="font-semibold mb-1">⚠️ Важно</div>
        Для FreeKassa и других платёжных систем, которые проверяют наличие баннера на сайте, код вставляется именно в исходный HTML. После «Сохранить» на превью применяется мгновенно. <b>На проде</b> (tempeo-group.uk) — нажмите «Save to GitHub» → «Deploy», чтобы изменения попали на сайт.
      </div>
    </Section>
  );
};

// Strip <script>...</script> and inline event handlers to prevent obvious XSS.
function stripScripts(html) {
  if (typeof html !== "string") return "";
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son\w+\s*=\s*'[^']*'/gi, "");
}

// =================== REGISTRY ===================
export const BLOCKS = {
  visibility: { label: "Видимость блоков", Component: VisibilityForm },
  hero: { label: "Главный экран", Component: HeroForm },
  stats: { label: "Статистика", Component: StatsForm },
  benefits: { label: "Преимущества", Component: BenefitsForm },
  pricing: { label: "Тарифы", Component: PricingForm },
  process: { label: "Процесс", Component: ProcessForm },
  countries: { label: "Страны", Component: CountriesForm },
  vacancies: { label: "Вакансии", Component: VacanciesForm },
  team_section: { label: "Заголовок «Команда»", Component: TeamSectionForm },
  reviews_section: { label: "Тексты «Отзывы»", Component: ReviewsSectionForm },
  contacts: { label: "Контакты", Component: ContactsForm },
  partners: { label: "Партнёры", Component: PartnersForm },
  payment: { label: "Страница оплаты", Component: PaymentForm },
  legal: { label: "Юр. документы", Component: LegalForm },
  license: { label: "Лицензия (PDF)", Component: LicenseForm },
  footer_banner: { label: "Баннер в подвале", Component: FooterBannerForm },
};
